/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import styled from '@emotion/styled';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { CT_THRESHOLDS } from '@resistapp/client/components/plots/chip-view-constant';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { AbunanceSelection } from '@resistapp/client/data-utils/filter-data/filter';
import { BarDatum, BoxDatum, getNumDetectedTotal } from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { useExperimentalMetric } from '@resistapp/client/hooks/use-experimental-metric';
import { useNormalisationMode, useXAxisMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { SmallExternalLinkIcon } from '@resistapp/client/hooks/use-science-reference';
import { GeneGrouping, L2Targets } from '@resistapp/common/assays';
import { isAdmin } from '@resistapp/common/features';
import { friendlyAbundance, friendlyDate, friendlyL2Target, type StandardDateFormat } from '@resistapp/common/friendly';
import { friendlyAMRFamily, friendlyDrugClass, friendlyResistanceMechanism } from '@resistapp/common/friendly-amr';
import { getNormalisedValue, NormalisedValueName } from '@resistapp/common/normalisation-mode';
import { assessTechReps } from '@resistapp/common/qa';
import {
  CardCategoryStat,
  CardIndex,
  CtsColumns,
  FullAbundance,
  NormalisationMode,
  XAxisMode,
} from '@resistapp/common/types';
import { LOD } from '@resistapp/common/utils';
import { SeriesPoint } from '@visx/shape/lib/types';
import { ScaleOrdinal } from 'd3-scale';
import { uniq, uniqBy, values } from 'lodash';
import { Fragment, useMemo, useState } from 'react';
import { getGroupTextColor } from '../../shared/palettes';

export type BarTooltipData = {
  bar: SeriesPoint<BarDatum>;
  key: string;
  index: number;
  height: number;
  width: number;
  y: number;
  color: string;
  label?: string;
};

export function getAbundace(datum: BarTooltipData) {
  const val = datum.bar.data[datum.key] as number | undefined;
  return +(val ?? 0);
}

interface BarTooltipContentProps {
  tooltipData: BarTooltipData;
}

interface NumDetectedTooltipContentProps extends BarTooltipContentProps {
  tooltipData: BarTooltipData;
  colorScale: ScaleOrdinal<string, string>;
  grouping: GeneGrouping;
  showGeneInfo?: boolean;
}

export function NumDetectedTooltipContent({
  tooltipData,
  colorScale,
  grouping,
  showGeneInfo = false,
}: NumDetectedTooltipContentProps) {
  const { allGeneGroups } = useAssayContext();
  const xAxisMode = useXAxisMode();
  const total = getNumDetectedTotal(tooltipData.bar.data);
  const groupColor = colorScale(tooltipData.key);
  const groupTextColor = getGroupTextColor(tooltipData.key, grouping, allGeneGroups);
  const sampleLabel =
    xAxisMode === XAxisMode.ORDINAL_INDIVIDUAL ? tooltipData.bar.data.bioNumber : tooltipData.bar.data.sampleNumber;

  const abundance = getAbundace(tooltipData);

  return (
    <div style={{ padding: 8 }}>
      <div>
        <strong>Sample {sampleLabel}:</strong> {tooltipData.bar.data.label}
        {tooltipData.bar.data.date &&
          ` on ${friendlyDate(tooltipData.bar.data.date as StandardDateFormat, 'prefixMon', true)}`}
      </div>
      <div style={{ marginTop: oldTheme.spacing.xs, fontSize: oldTheme.fontSize.s }}>
        <strong>{total} genes detected in total</strong> in selected targets
      </div>
      {showGeneInfo && (
        <div style={{ marginTop: oldTheme.spacing.min, fontSize: oldTheme.fontSize.s }}>
          <strong>
            {abundance}{' '}
            <div
              style={{
                backgroundColor: groupColor,
                color: groupTextColor,
                borderRadius: '6px',
                padding: '2px 2px',
                display: 'inline-block',
              }}
            >
              {tooltipData.label ||
                (tooltipData.key in L2Targets ? friendlyL2Target(tooltipData.key as L2Targets) : tooltipData.key)}
            </div>{' '}
            genes detected
          </strong>
        </div>
      )}
    </div>
  );
}

interface HeatmapTooltipContentProps extends BarTooltipContentProps {
  grouping: GeneGrouping;
  colorScale: ScaleOrdinal<string, string>;
}

export function HeatmapTooltipContent({ colorScale, tooltipData, grouping }: HeatmapTooltipContentProps) {
  const { allGeneGroups, getGroup, allAssays } = useAssayContext();
  const normalisationMode = useNormalisationMode();
  const valueName = NormalisedValueName[normalisationMode];
  const groupName = getGroup(tooltipData.key, grouping) as string;
  const groupColor = colorScale(groupName);
  const groupTextColor = getGroupTextColor(groupName, grouping, allGeneGroups);
  const value = getAbundace(tooltipData);

  // Access research context to determine current abundance mode (e.g. Estimates)
  const { project, adminFeatures, queryFilters } = useResearchContext();

  // Flags for current abundance mode
  const abundanceMode = queryFilters?.filters?.abundances;
  const isEstimatesMode = abundanceMode === AbunanceSelection.ESTIMATES;
  const isMeasuredOrEstimateMode = abundanceMode === AbunanceSelection.MEASURED_OR_ESTIMATE;

  // Retrieve the original relative abundance from sample data
  const { abundanceByMode, measured } = useMemo(() => {
    if (!project) return { abundanceByMode: null, measured: undefined };
    const sampleId = Number(tooltipData.bar.data.sampleId);
    const assay = getGroup(tooltipData.key, 'assay') ?? tooltipData.key;
    const sample = values(project.samplesByUID)
      .flat()
      .find(s => s.id === sampleId);
    const abundance = sample?.abundances.find(a => a.assay === assay);
    if (!abundance) return { abundanceByMode: null, measured: undefined };

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const build = {} as Record<NormalisationMode, number | null>;
    values(NormalisationMode).forEach(mode => {
      // try estimate or measured value depending on flags; but we only need measured here later
      build[mode] = getNormalisedValue(abundance, mode);
    });
    return { abundanceByMode: build, measured: abundance.measured };
  }, [project, tooltipData, getGroup]);

  const getEstimateForMode = (abundance: FullAbundance, mode: NormalisationMode): number | null =>
    abundance.estimates?.[mode] ?? null;

  let displayedValue: number | null | undefined = value;
  if (isEstimatesMode) {
    const sampleId = Number(tooltipData.bar.data.sampleId);
    const assay = getGroup(tooltipData.key, 'assay') ?? tooltipData.key;
    const sample = project
      ? values(project.samplesByUID)
          .flat()
          .find(s => s.id === sampleId)
      : undefined;
    const abundance = sample?.abundances.find(a => a.assay === assay);
    displayedValue = abundance ? getEstimateForMode(abundance, normalisationMode) : null;
  } else if (isMeasuredOrEstimateMode) {
    // Show N/A when gene is not measured
    displayedValue = measured ? value : null;
  }

  const { user } = useUser();
  const isAdminUser = isAdmin(user);
  const showAdmin = isAdminUser && adminFeatures.isModifyAsAdminActive;

  const adminInfo = useMemo(() => {
    if (!showAdmin || !project) return null;
    const assay = getGroup(tooltipData.key, 'assay') ?? tooltipData.key;
    const assayInfo = allAssays.find(a => a.assay === assay);
    const currentCtsByAssayFromData = tooltipData.bar.data.ctsByAssay;

    const ctsReps: CtsColumns[] =
      currentCtsByAssayFromData &&
      typeof currentCtsByAssayFromData === 'object' && // Ensure it's an object
      !Array.isArray(currentCtsByAssayFromData) && // Ensure it's not an array itself
      currentCtsByAssayFromData[assay] && // Ensure the key exists
      Array.isArray(currentCtsByAssayFromData[assay]) // Ensure the value for the key is an array
        ? currentCtsByAssayFromData[assay] // Now TypeScript should be more confident that this is CtReplicate[]
        : [];

    if (!ctsReps.length) return null;

    const tmMinLocal = assayInfo?.minMeltingTemperature ?? null;
    const tmMaxLocal = assayInfo?.maxMeltingTemperature ?? null;
    const qaRes = assessTechReps(
      ctsReps.map(r => ({ ct: r.ct ?? null, efficiency: r.efficiency ?? null, tm: r.tm ?? null })),
      assay,
      tmMinLocal,
      tmMaxLocal,
      CT_THRESHOLDS[5],
    );
    const finalReps = qaRes.reps;
    const stdCtFinal = qaRes.stdCt;

    const nbsp = '\u00A0';
    const spacer = `${nbsp}${nbsp}${nbsp}`; // three non-breaking spaces

    const buildStyledList = (vals: Array<number | null>, field: 'ct' | 'efficiency' | 'tm') =>
      finalReps.map((rep, idx) => {
        const style: React.CSSProperties = { fontFamily: 'monospace' };

        if (rep.rule != null) style.textDecoration = 'line-through';

        let color: string | undefined;
        if (field === 'efficiency') {
          color = rep.rule === 4 ? 'orange' : rep.rule === 2 ? 'red' : undefined;
        } else if (field === 'ct') {
          color = rep.rule === 3 ? 'orange' : rep.rule === 1 ? 'red' : undefined;
        } else if (field === 'tm') {
          color = rep.rule === 0 ? 'red' : undefined;
        }
        if (color) style.color = color;

        const val = vals[idx];
        const text = val == null ? '--.--' : val.toFixed(2);
        return (
          <span key={idx}>
            <code style={style}>
              {field === 'efficiency' && val != null ? '\u00A0' : ''}
              {text}
            </code>
            {idx < finalReps.length - 1 ? spacer : ''}
          </span>
        );
      });

    const ctsStyled = buildStyledList(
      finalReps.map(r => r.ct),
      'ct',
    );
    const effStyled = buildStyledList(
      finalReps.map(r => r.efficiency),
      'efficiency',
    );
    const tmStyled = buildStyledList(
      finalReps.map(r => r.tm),
      'tm',
    );

    // Use non-breaking spaces (Unicode \u00A0) to preserve spacing in rendered HTML.
    // We also replace comma separators with three non-breaking spaces for a cleaner look.

    // Build status numbers so that they align vertically with the other technical-rep
    // columns.  Each numeric entry is rendered inside a monospace <code> element and
    // left-padded with non-breaking spaces to four characters (matching the width of
    // the 5-character numeric fields plus the three-space separator used below).
    const statusStyled = finalReps.map((rep, idx) => {
      const paddedRule = nbsp + nbsp + String(rep.rule ?? nbsp) + nbsp + nbsp; // 4-char field
      return (
        <span key={idx}>
          <code style={{ fontFamily: 'monospace' }}>{paddedRule}</code>
          {idx < finalReps.length - 1 ? spacer : ''}
        </span>
      );
    });

    const tmMin = assayInfo?.minMeltingTemperature;
    const tmMax = assayInfo?.maxMeltingTemperature;
    const tmRangeText = tmMin == null && tmMax == null ? '' : ` (r: ${tmMin ?? ''}-${tmMax ?? ''})`;

    const flags = Array.from(
      new Set(ctsReps.flatMap((ctRep: CtsColumns) => ctRep.flags?.split(/[,;\s]+/).filter(Boolean) || [])),
    );
    const first: CtsColumns | undefined = ctsReps[0];
    // Add a check for first being undefined, as ctsReps could be empty (for pooled projects, though unlikely due to earlier check?)
    if (!first) return null;
    const chip = project.chips.find(c => c.id === first.chipId);
    const chipInfo = chip ? `${chip.name} (${first.row},${first.column})` : `(${first.row},${first.column})`;

    return {
      ctsStyled,
      effStyled,
      tmStyled,
      statusStyled,
      stdCt: stdCtFinal,
      flags,
      chipInfo,
      tmRangeText,
    };
  }, [showAdmin, tooltipData, getGroup, allAssays, project]);

  const estimateInfo = useMemo(() => {
    if (!showAdmin || !project) return null;
    const sampleId = Number(tooltipData.bar.data.sampleId);
    const assay = getGroup(tooltipData.key, 'assay') ?? tooltipData.key;
    const sample = values(project.samplesByUID)
      .flat()
      .find(s => s.id === sampleId);
    const abundance = sample?.abundances.find(a => a.assay === assay);
    if (!abundance) return null;
    const est = getEstimateForMode(abundance, normalisationMode);
    if (est == null) return null;
    const low = abundance.estimateLows?.[normalisationMode] ?? null;
    const high = abundance.estimateHighs?.[normalisationMode] ?? null;
    return {
      estimate: est,
      low,
      high,
    };
  }, [showAdmin, project, tooltipData, getGroup, normalisationMode]);

  const { familyElements, mechanismElements } = useMemo(() => {
    const assayInfos = allAssays.filter(a =>
      grouping === 'assay' ? a.assay === groupName : getGroup(a.assay, grouping) === groupName,
    );

    const matches = assayInfos.flatMap(info => (info.card ? info.card.matches : []));
    const families = assayInfos.flatMap(info => (info.card ? info.card.amrFamilies : []));

    const familyStats = families.reduce<Record<string, CardCategoryStat>>((acc, f) => {
      if (Object.prototype.hasOwnProperty.call(acc, f.name)) {
        acc[f.name].coveredGenes += f.coveredGenes;
      } else {
        acc[f.name] = { ...f };
      }
      return acc;
    }, {});

    const nbsp = '\u00A0';

    const familyEntries = values(familyStats);

    const splitValues = (raw: string | null | undefined) =>
      raw
        ? raw
            .split(';')
            .map(v => v.trim())
            .filter(Boolean)
        : [];

    const mechanismStrings = uniq(matches.flatMap(m => splitValues(m.resistanceMechanism))).map(raw =>
      friendlyResistanceMechanism(raw).replace(/ /g, nbsp),
    );

    const mechanismElementsLocal = mechanismStrings.map((txt, idx) => (
      <span key={idx} style={{ whiteSpace: 'nowrap' }}>
        {txt}
        {idx < mechanismStrings.length - 1 && <br />}
      </span>
    ));

    const familyElementsLocal = familyEntries.map((f, idx) => {
      const nameText = friendlyAMRFamily(f.name).replace(/ /g, nbsp);
      return (
        <span key={idx} style={{ whiteSpace: 'nowrap' }}>
          {nameText}
          <span style={{ color: '#666' }}>
            {' '}
            ({f.coveredGenes}/{f.totalGenes})
          </span>
          {idx < familyEntries.length - 1 && <br />}
        </span>
      );
    });

    return { familyElements: familyElementsLocal, mechanismElements: mechanismElementsLocal };
  }, [allAssays, groupName, grouping, getGroup]);

  return (
    <div style={{ padding: 8 }}>
      <table style={{ width: '100%' }}>
        <tbody>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Group:</Label>
            <Value>
              <span
                style={{ backgroundColor: groupColor, color: groupTextColor, borderRadius: '6px', padding: '1px 4px' }}
              >
                {groupName in L2Targets ? friendlyL2Target(groupName as L2Targets) : groupName}
              </span>
            </Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Sample:</Label>
            <Value>
              {tooltipData.bar.data.bioNumber} ({tooltipData.bar.data.sampleId})
            </Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Name:</Label>
            <Value>{tooltipData.bar.data.label}</Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Gene:</Label>
            <Value>
              {tooltipData.key}
              {showAdmin && ` (${getGroup(tooltipData.key, 'assay') ?? tooltipData.key})`}
            </Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>{valueName}:</Label>
            <Value>{isMeasuredOrEstimateMode && measured === false ? 'N/A' : friendlyAbundance(displayedValue)}</Value>
          </tr>
          {showAdmin &&
            estimateInfo &&
            (() => {
              const { low, high, estimate } = estimateInfo;
              const actual = abundanceByMode ? abundanceByMode[normalisationMode] : null;
              // Highlight when measured value is outside the estimated range *or*
              // when the gene was not measured (actual == null) but the lower bound of the estimate
              // is clearly above the Limit of Detection (LOD).
              const isOutside =
                (actual != null && low != null && high != null && (actual < low || actual > high)) ||
                (actual == null && low != null && low > LOD);
              const rangeStyle: React.CSSProperties | undefined = isOutside
                ? { color: '#8B0000' } // dark red
                : undefined;
              return (
                <tr style={{ paddingBottom: '12px' }}>
                  <Label>{valueName} estim.:</Label>
                  <Value>
                    {friendlyAbundance(estimate, { significantN: 2, showLod: true })}{' '}
                    <span style={rangeStyle}>
                      ({friendlyAbundance(low, { significantN: 2 })}-{friendlyAbundance(high, { significantN: 2 })})
                    </span>
                  </Value>
                </tr>
              );
            })()}
          {showAdmin && (
            <>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>AMR gene&nbsp;families:</Label>
                <Value>
                  {!familyElements.length
                    ? 'N/A'
                    : familyElements.length > 10
                      ? `${familyElements.length} families`
                      : familyElements}
                </Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Resistance&nbsp;Mechanisms:</Label>
                <Value>{mechanismElements.length ? mechanismElements : 'N/A'}</Value>
              </tr>
            </>
          )}
          {adminInfo && (
            <>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Chip:</Label>
                <Value>{adminInfo.chipInfo}</Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Ct values:</Label>
                <Value>
                  {adminInfo.ctsStyled}
                  {adminInfo.stdCt != null && (
                    <span style={{ fontStyle: 'italic' }}> (σ {adminInfo.stdCt.toFixed(2)})</span>
                  )}
                </Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Efficiencies:</Label>
                <Value>{adminInfo.effStyled}</Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Tm values:</Label>
                <Value>
                  {adminInfo.tmStyled}
                  {adminInfo.tmRangeText}
                </Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>QA rule:</Label>
                <Value>{adminInfo.statusStyled}</Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Flags:</Label>
                <Value>{adminInfo.flags.join(', ') || '-'}</Value>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

interface GeneInfoTooltipContentProps {
  assay: string;
}

function CardGeneList({ matches }: { matches: CardIndex[] }) {
  // Show 3 genes initially, then expand in batches of 25 thereafter
  const FIRST_VISIBLE = 3;
  const BATCH_SIZE = 25;

  const [visibleCount, setVisibleCount] = useState(FIRST_VISIBLE);

  const uniqueMatches = uniqBy(matches, m => m.cardShortName ?? m.cvtermId);
  if (!uniqueMatches.length) return <>N/A</>;

  const handleExpand = () => {
    setVisibleCount(prev => {
      // If we're still showing the initial batch, jump to the first full batch (25 or total length)
      if (prev === FIRST_VISIBLE) {
        return Math.min(BATCH_SIZE, uniqueMatches.length);
      }
      // Otherwise add another batch of 25
      return Math.min(prev + BATCH_SIZE, uniqueMatches.length);
    });
  };

  const displayed = uniqueMatches.slice(0, visibleCount);

  const elements: React.ReactNode[] = [];

  displayed.forEach((m, idx) => {
    const accession = m.dnaAccession ?? m.proteinAccession;

    const geneElem = (
      <Fragment key={m.cvtermId ?? m.cardShortName ?? idx}>
        <a href={`https://card.mcmaster.ca/ontology/${m.cvtermId}`} target="_blank" rel="noopener noreferrer">
          {m.cardShortName}
        </a>
        {accession && (
          <a
            href={`https://www.ncbi.nlm.nih.gov/nuccore/${accession}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: '2px', lineHeight: 1 }}
          >
            <SmallExternalLinkIcon
              icon={icon(faExternalLinkAlt)}
              style={{ verticalAlign: 'middle', position: 'relative', top: '-1px', marginRight: '2px' }}
            />
          </a>
        )}
      </Fragment>
    );

    elements.push(geneElem);

    const isLastDisplayed = idx === displayed.length - 1;
    if (!isLastDisplayed) {
      const shouldBreak = (idx + 1) % 5 === 0;
      elements.push(shouldBreak ? <br key={`br-${idx}`} /> : ' ');
    }
  });

  const hasMore = visibleCount < uniqueMatches.length;

  if (hasMore) {
    const isInitial = visibleCount === FIRST_VISIBLE;

    if (!isInitial) {
      // Put ellipsis on its own line for subsequent batches
      elements.push(<br key="br-final" />);
    } else {
      // Space before ellipsis to separate from the third gene
      elements.push(' ');
    }

    elements.push(
      <span key="ellipsis" style={{ cursor: 'pointer' }} onClick={handleExpand}>
        ...
      </span>,
    );
  }

  return <>{elements}</>;
}

export function GeneInfoTooltipContent({ assay }: GeneInfoTooltipContentProps) {
  const { allAssays } = useAssayContext();

  // Determine the relevant assays. If the provided key is an assay (starts with 'AY'),
  // we use it directly. Otherwise we treat the key as a gene name and collect all assays
  // whose gene field matches the provided key.
  const relevantAssays = assay.startsWith('AY')
    ? allAssays.filter(a => a.assay === assay)
    : allAssays.filter(a => a.gene === assay);

  const matches = relevantAssays.flatMap(info => info.card?.matches ?? []);
  const families = relevantAssays.flatMap(info => info.card?.amrFamilies ?? []);

  const familyStats = families.reduce<Record<string, CardCategoryStat>>((acc, f) => {
    if (Object.prototype.hasOwnProperty.call(acc, f.name)) {
      acc[f.name].coveredGenes += f.coveredGenes;
    } else {
      acc[f.name] = { ...f };
    }
    return acc;
  }, {});

  const nbsp = '\u00A0';

  const familyEntries = values(familyStats);

  const splitValues = (raw: string | null | undefined) =>
    raw
      ? raw
          .split(';')
          .map(v => v.trim())
          .filter(Boolean)
      : [];

  const mechanismStrings = uniq(matches.flatMap(m => splitValues(m.resistanceMechanism))).map(raw =>
    friendlyResistanceMechanism(raw).replace(/ /g, nbsp),
  );

  // Drug classes
  const drugClassStrings = uniq(matches.flatMap(m => splitValues(m.drugClass))).map(raw =>
    friendlyDrugClass(raw).replace(/ /g, nbsp),
  );

  // Dynamic label texts with correct singular/plural forms and optional counts
  const familyLabelText = `From ${familyEntries.length > 1 ? `${familyEntries.length} ` : ''}AMR gene\u00A0${
    familyEntries.length > 1 ? 'families' : 'family'
  }:`;
  const mechanismLabelText = `With ${mechanismStrings.length > 1 ? `${mechanismStrings.length} ` : ''}resistance\u00A0mechanism${
    mechanismStrings.length > 1 ? 's' : ''
  }:`;
  const drugClassLabelText = `Against ${drugClassStrings.length > 1 ? `${drugClassStrings.length} ` : ''}drug\u00A0class${
    drugClassStrings.length > 1 ? 'es' : ''
  }:`;

  // Build table rows for aligned layout
  const familyRows = familyEntries.length ? (
    familyEntries.map((f, idx) => {
      const nameText = friendlyAMRFamily(f.name).replace(/ /g, nbsp);
      return (
        <tr key={`fam-${idx}`} style={{ paddingBottom: '4px' }}>
          {idx === 0 ? <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{familyLabelText}</Label> : <Label />}
          <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>
            <a href={`https://card.mcmaster.ca/ontology/${f.cvtermId}`} target="_blank" rel="noopener noreferrer">
              {nameText}
            </a>
            <span style={{ color: '#666' }}>
              {' '}
              ({f.coveredGenes}/{f.totalGenes})
            </span>
          </Value>
        </tr>
      );
    })
  ) : (
    <tr>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{familyLabelText}</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>N/A</Value>
    </tr>
  );

  const mechanismRows = mechanismStrings.length ? (
    mechanismStrings.map((txt, idx) => (
      <tr key={`mech-${idx}`} style={{ paddingBottom: '4px' }}>
        {idx === 0 ? <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{mechanismLabelText}</Label> : <Label />}
        <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>{txt}</Value>
      </tr>
    ))
  ) : (
    <tr>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{mechanismLabelText}</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>N/A</Value>
    </tr>
  );

  const drugClassRows = drugClassStrings.length ? (
    drugClassStrings.map((txt, idx) => (
      <tr key={`drug-${idx}`} style={{ paddingBottom: '4px' }}>
        {idx === 0 ? <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{drugClassLabelText}</Label> : <Label />}
        <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>{txt}</Value>
      </tr>
    ))
  ) : (
    <tr>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{drugClassLabelText}</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>N/A</Value>
    </tr>
  );

  // Assay row (display all relevant assay IDs comma-separated)
  const assayStr = relevantAssays.map(a => a.assay).join(', ');
  const assayName = relevantAssays.map(a => a.gene).join(', ');
  const assayNameRow = (
    <tr style={{ paddingBottom: '4px' }}>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>Assay:</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>
        {assayName} ({assayStr})
      </Value>
    </tr>
  );

  const uniqueMatches = uniqBy(matches, m => m.cardShortName ?? m.cvtermId);
  const cardLabelText = `Binds with ${uniqueMatches && uniqueMatches.length > 1 ? `${uniqueMatches.length} ` : ''}${uniqueMatches.length > 1 ? 'ARGs' : uniqueMatches.length === 1 ? 'ARG' : 'CARD gene'}:`;

  const cardRow = (
    <tr style={{ paddingBottom: '4px' }}>
      <Label style={{ fontWeight: oldTheme.fontWeight.bold }}>{cardLabelText}</Label>
      <Value style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>
        <CardGeneList matches={matches} />
      </Value>
    </tr>
  );

  return (
    <div style={{ fontSize: oldTheme.fontSize.s }}>
      <table style={{ width: '100%' }}>
        <tbody>
          {assayNameRow}
          {cardRow}
          {mechanismRows}
          {drugClassRows}
          {familyRows}
        </tbody>
      </table>
    </div>
  );
}

const Label = styled.td`
  ${oldTheme.fontWeight.bold}
  width: 230px;
  text-align: right;
  white-space: nowrap;
  vertical-align: top;
`;

const Value = styled.td`
  width: 230px;
  text-align: left;
  vertical-align: top;
`;

interface BoxTooltipContentProps {
  tooltipData: BoxDatum;
}

export function BoxTooltipContent({ tooltipData }: BoxTooltipContentProps) {
  const normalisationMode = useNormalisationMode();
  const xAxisMode = useXAxisMode();
  const sampleLabel = xAxisMode === XAxisMode.ORDINAL_INDIVIDUAL ? tooltipData.bioNumber : tooltipData.sampleNumber;
  const showExperimentalMetrics = !!useExperimentalMetric();
  return (
    <>
      <div style={{ color: oldTheme.darkGray, padding: 8 }}>
        <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
          <strong>Sample:</strong> {sampleLabel} ({tooltipData.sampleId})
        </div>
        <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
          <strong>Name: </strong> {tooltipData.label}
        </div>
        {tooltipData.date ? (
          <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
            <strong>Date:</strong> {friendlyDate(tooltipData.date as StandardDateFormat, 'prefixMon', true)}
          </div>
        ) : null}
      </div>
      <div style={{ fontSize: oldTheme.fontSize.s }}>
        {showExperimentalMetrics && (
          <>
            {Object.entries(tooltipData.experimentalMetrics || {}).map(([key, value]) => (
              <div key={key}>
                <b>{key}: </b>
                {(value as number | undefined)?.toFixed(key.startsWith('A') ? 1 : 0)}
              </div>
            ))}
            <br />
          </>
        )}
        <br />
        <table style={{ width: '100%' }}>
          <tbody>
            <tr>
              <td />
              <td style={{ textAlign: 'left', fontWeight: 'bold' }}>
                {NormalisedValueNameShort[normalisationMode]}&nbsp;
              </td>
              <td style={{ textAlign: 'left', fontWeight: 'bold' }}>log₁₀</td>
            </tr>
            {tooltipData.max === tooltipData.min && (
              <tr>
                <td style={{ textAlign: 'right' }} />
                <td>{tooltipData.max ? friendlyAbundance(10 ** tooltipData.max) : '-'}</td>
                <td>{tooltipData.max?.toFixed(6)}</td>
              </tr>
            )}
            {tooltipData.max !== tooltipData.min && (
              <>
                <tr>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 10 }}>T</span>&nbsp;&nbsp;&nbsp;&nbsp; upper
                  </td>
                  <td>{tooltipData.max ? friendlyAbundance(10 ** tooltipData.max) : '-'}</td>
                  <td>{tooltipData.max?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>Q3</td>
                  <td>{tooltipData.thirdQuartile ? friendlyAbundance(10 ** tooltipData.thirdQuartile) : '-'}</td>
                  <td>{tooltipData.thirdQuartile?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>×&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;mean</td>
                  <td>{tooltipData.mean ? friendlyAbundance(10 ** tooltipData.mean) : '-'}</td>
                  <td>{tooltipData.mean?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>—&nbsp;median</td>
                  <td>{tooltipData.median ? friendlyAbundance(10 ** tooltipData.median) : '-'}</td>
                  <td>{tooltipData.median?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>Q1</td>
                  <td>{tooltipData.firstQuartile ? friendlyAbundance(10 ** tooltipData.firstQuartile) : '-'}</td>
                  <td>{tooltipData.firstQuartile?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>⊥&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;lower</td>
                  <td>{tooltipData.min ? friendlyAbundance(10 ** tooltipData.min) : '-'}</td>
                  <td>{tooltipData.min?.toFixed(6)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

const NormalisedValueNameShort = {
  [NormalisationMode.SIXTEEN_S]: 'Rel. abund.',
  [NormalisationMode.MG_SS]: 'Copy num. per mg SS',
  [NormalisationMode.HOUR]: 'Copy num. per hour',
  [NormalisationMode.LITRE]: 'Copy num. per L',
  [NormalisationMode.TEN_UL_DILUTED_DNA]: 'Copy num.',
  [NormalisationMode.MG_BOD]: 'Copy num. per mg BOD',
} as const;
